import { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSoc, useSocIdentities } from '@/api/soc';
import { useGaps } from '@/api/insights';
import { PageHeader } from '@/components/PageHeader';
import { StatCard } from '@/components/dashboard/StatCard';
import { CategoryBar, Donut } from '@/components/dashboard/charts';
import { DesktopOnly } from '@/components/dashboard/DesktopOnly';
import { CompanyCell, PageLoading, Panel } from '@/components/network/NetworkKit';
import { HEALTH_LABEL, IncidentStatusChip, SeverityChip } from '@/components/soc/SocKit';
import { fmtRelative } from '@/lib/cobalt-helpers';
import { ROUTES, insightsGapsView, socIncidentPath, socView } from '@/lib/routes';
import type { AgentHealth } from '@/api/soc';

/**
 * SOC overview (`/soc`) — Huntress across every client (or the picked one): open incidents, agent
 * health, computers at Huntress clients with no agent (gap list), ITDR identities without MFA,
 * incident flow, and one row per client. Everything links to a filtered list.
 */

const DAY = 86_400_000;
const HEALTH_COLOR: Record<AgentHealth, string> = {
  healthy: 'hsl(var(--success))', defender: 'hsl(var(--warning))', 'no-edr': 'hsl(var(--warning) / 0.6)', silent: 'hsl(var(--destructive) / 0.55)', stale: 'hsl(var(--neutral) / 0.45)', isolated: 'hsl(var(--destructive))',
};

export default function SocOverviewPage() {
  const navigate = useNavigate();
  const { data, isLoading, error, isFetching } = useSoc({ scoped: true });
  const ids = useSocIdentities({ scoped: true });
  const gaps = useGaps({ scoped: true });

  const k = useMemo(() => {
    if (!data?.configured) return null;
    const recent = (days: number) => data.incidents.filter((i) => i.sentAt && Date.now() - new Date(i.sentAt).getTime() < days * DAY);
    const count = (h: AgentHealth) => data.agents.filter((a) => a.health === h).length;
    const noEdr = (gaps.data?.groups ?? []).filter((g) => g.kind === 'no-edr').reduce((n, g) => n + g.items.length, 0);
    // Week labels are "26/7": twelve "26 Jul" labels overlap on the axis.
    const weeks = Array.from({ length: 12 }, (_, w) => {
      const start = new Date(); start.setHours(0, 0, 0, 0); start.setDate(start.getDate() - start.getDay() - 7 * (11 - w));
      return { id: w, label: start.toLocaleDateString('en-AU', { day: 'numeric', month: 'numeric' }), from: start.getTime(), value: 0, color: 'var(--chart-series-1)' };
    });
    for (const i of data.incidents) { const t = i.sentAt ? new Date(i.sentAt).getTime() : NaN; const w = weeks.find((x) => t >= x.from && t < x.from + 7 * DAY); if (w) w.value++; }
    const perClient = new Map<string, { companyId: number | null; name: string; agents: number; silent: number; stale: number; incidents90: number; open: number }>();
    for (const a of data.agents) {
      const key = String(a.companyId ?? a.orgName);
      const r = perClient.get(key) ?? { companyId: a.companyId, name: a.companyName ?? a.orgName, agents: 0, silent: 0, stale: 0, incidents90: 0, open: 0 };
      r.agents++; if (a.health === 'silent') r.silent++; if (a.health === 'stale') r.stale++;
      perClient.set(key, r);
    }
    for (const i of recent(90)) { const r = perClient.get(String(i.companyId ?? i.orgName)); if (r) r.incidents90++; }
    for (const i of data.incidents.filter((x) => x.status !== 'closed')) { const r = perClient.get(String(i.companyId ?? i.orgName)); if (r) r.open++; }
    return {
      open: data.incidents.filter((i) => i.status !== 'closed').length,
      last30: recent(30).length,
      critHigh90: recent(90).filter((i) => /critical|high/i.test(i.severity ?? '')).length,
      reporting: data.agents.filter((a) => a.health !== 'stale' && a.health !== 'silent').length,
      silent: count('silent'), stale: count('stale'), defender: count('defender'), noEdr,
      health: (Object.keys(HEALTH_LABEL) as AgentHealth[]).map((h) => ({ id: h, label: HEALTH_LABEL[h], value: count(h), color: HEALTH_COLOR[h] })).filter((b) => b.value),
      weeks,
      clients: [...perClient.values()].sort((a, b) => b.open - a.open || b.silent - a.silent || a.name.localeCompare(b.name)),
    };
  }, [data, gaps.data]);
  const noMfa = ids.data?.items ? ids.data.items.filter((i) => i.enabled && i.billable && i.mfa === false).length : null;

  return (
    <DesktopOnly title="SOC is available on desktop" message="Open Cosmos on a larger screen to view Huntress.">
      <div className="mx-auto w-full max-w-[1600px] px-4 py-5 md:px-6">
        <PageHeader title="SOC · Huntress" isFetching={isFetching}
          subtitle={data?.configured ? <>Managed EDR + ITDR across {data.orgs.length} organisation{data.orgs.length === 1 ? '' : 's'} · read {fmtRelative(data.fetchedAt)}</> : 'Huntress managed EDR / ITDR / SIEM'} />
        <PageLoading isLoading={isLoading} error={error} configured={data?.configured} vendor="Huntress" />
        {data?.configured && k && (
          <>
            <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-4 2xl:grid-cols-8">
              <StatCard label="Open incidents" value={k.open} tone={k.open ? 'warning' : 'success'} onClick={() => navigate(socView(ROUTES.soc.incidents, { show: 'open' }))} />
              <StatCard label="Incidents · 30 days" value={k.last30} onClick={() => navigate(socView(ROUTES.soc.incidents, { show: 'recent' }))} />
              <StatCard label="Critical / high · 90d" value={k.critHigh90} tone={k.critHigh90 ? 'destructive' : 'default'} onClick={() => navigate(socView(ROUTES.soc.incidents, { show: 'serious' }))} />
              <StatCard label="Agents reporting" value={k.reporting} sub={`of ${data.agents.length}`} onClick={() => navigate(ROUTES.soc.agents)} />
              <StatCard label="Not checking in" value={k.silent} tone={k.silent ? 'destructive' : 'success'} sub={`> ${data.settings.silentHours} h`} onClick={() => navigate(socView(ROUTES.soc.agents, { show: 'silent' }))} />
              <StatCard label="Defender unhealthy" value={k.defender} tone={k.defender ? 'warning' : 'success'} onClick={() => navigate(socView(ROUTES.soc.agents, { show: 'defender' }))} />
              <StatCard label="Without Huntress" value={gaps.data ? k.noEdr : '…'} tone={k.noEdr ? 'destructive' : 'success'} sub="RMM computers" onClick={() => navigate(insightsGapsView({ kind: 'no-edr' }))} />
              <StatCard label="ITDR · no MFA" value={noMfa ?? '…'} tone={noMfa ? 'warning' : 'default'} sub="billable identities" onClick={() => navigate(socView(ROUTES.soc.identities, { show: 'no-mfa' }))} />
            </div>

            <div className="mt-4 grid grid-cols-1 gap-4 xl:grid-cols-3">
              <Panel title="Agent health" more={{ to: ROUTES.soc.agents, label: 'Agents' }}>
                <div className="p-3"><Donut data={k.health} height={220} onSelect={(b) => navigate(socView(ROUTES.soc.agents, { show: String(b.id) }))} /></div>
                {k.stale > 0 && <p className="px-4 pb-3 text-xs text-muted-foreground">{k.stale} stale agent{k.stale === 1 ? '' : 's'} (no callback for {data.settings.staleDays}+ days): likely retired machines to remove in Huntress.</p>}
              </Panel>
              <Panel title="Incidents per week · 12 weeks" more={{ to: ROUTES.soc.incidents, label: 'Incidents' }}>
                <div className="p-3"><CategoryBar data={k.weeks} height={240} name="Incidents" /></div>
              </Panel>
              <Panel title="Recent incidents" more={{ to: ROUTES.soc.incidents, label: 'All' }}>
                <ul className="divide-y divide-divider">
                  {data.incidents.slice(0, 8).map((i) => (
                    <li key={i.id}>
                      <Link to={socIncidentPath(i.id)} className="block px-4 py-2 hover:bg-muted/40">
                        <div className="flex items-center gap-2"><SeverityChip severity={i.severity} /><IncidentStatusChip status={i.status} /><span className="ml-auto text-[11px] text-muted-foreground">{fmtRelative(i.sentAt)}</span></div>
                        <div className="mt-0.5 truncate text-sm text-foreground">{i.summary ?? i.subject}</div>
                        <div className="text-[11px] text-muted-foreground">{i.companyName ?? i.orgName}</div>
                      </Link>
                    </li>
                  ))}
                  {data.incidents.length === 0 && <li className="px-4 py-6 text-center text-sm text-muted-foreground">No incidents.</li>}
                </ul>
              </Panel>
            </div>

            <div className="mt-4">
              <Panel title="By client" more={{ to: ROUTES.soc.organizations, label: 'Organisations' }}>
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <tr><th className="px-4 py-2 font-medium">Client</th><th className="px-3 py-2 text-right font-medium">Agents</th><th className="px-3 py-2 text-right font-medium">Not checking in</th><th className="px-3 py-2 text-right font-medium">Stale</th><th className="px-3 py-2 text-right font-medium">Incidents · 90d</th><th className="px-3 py-2 text-right font-medium">Open</th></tr>
                  </thead>
                  <tbody className="divide-y divide-divider">
                    {k.clients.map((c) => (
                      <tr key={c.name} className="hover:bg-muted/30">
                        <td className="max-w-[320px] px-4 py-2"><CompanyCell companyId={c.companyId} companyName={c.name} fallback={c.name} /></td>
                        <td className="px-3 py-2 text-right tabular-nums">{c.agents}</td>
                        <td className={`px-3 py-2 text-right tabular-nums ${c.silent ? 'text-destructive' : 'text-muted-foreground'}`}>{c.silent}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{c.stale}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{c.incidents90}</td>
                        <td className={`px-3 py-2 text-right tabular-nums ${c.open ? 'font-semibold text-warning' : 'text-muted-foreground'}`}>{c.open}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Panel>
            </div>
          </>
        )}
      </div>
    </DesktopOnly>
  );
}
